import React, { useState, useEffect, useCallback } from 'react';
import './SnakeGame.css';

const GRID_SIZE = 20;
const CELL_SIZE = 20;
const INITIAL_SPEED = 150;
const FOOD_EMOJIS = ['💻', '🚀', '⚡', '🔥', '💡', '🎮'];

function SnakeGame({ onClose, darkMode }) {
    const [snake, setSnake] = useState([[10, 10]]);
    const [food, setFood] = useState([5, 5]);
    const [direction, setDirection] = useState('RIGHT');
    const [gameOver, setGameOver] = useState(false);
    const [score, setScore] = useState(0);
    const [currentEmoji, setCurrentEmoji] = useState(FOOD_EMOJIS[0]);
    const [paused, setPaused] = useState(false);
    const [touchStart, setTouchStart] = useState(null);

    const getRandomPosition = useCallback(() => {
        return [
            Math.floor(Math.random() * GRID_SIZE),
            Math.floor(Math.random() * GRID_SIZE)
        ];
    }, []);

    const getRandomEmoji = useCallback(() => {
        return FOOD_EMOJIS[Math.floor(Math.random() * FOOD_EMOJIS.length)];
    }, []);

    const checkCollision = useCallback((head) => {
        // Wall collision
        if (
            head[0] < 0 || head[0] >= GRID_SIZE ||
            head[1] < 0 || head[1] >= GRID_SIZE
        ) return true;

        // Self collision
        for (let i = 1; i < snake.length; i++) {
            if (head[0] === snake[i][0] && head[1] === snake[i][1]) return true;
        }

        return false;
    }, [snake]);

    const moveSnake = useCallback(() => {
        if (gameOver || paused) return;

        const newSnake = [...snake];
        const head = [...newSnake[0]];

        switch (direction) {
            case 'UP': head[1] -= 1; break;
            case 'DOWN': head[1] += 1; break;
            case 'LEFT': head[0] -= 1; break;
            case 'RIGHT': head[0] += 1; break;
            default: break;
        }

        if (checkCollision(head)) {
            setGameOver(true);
            return;
        }

        newSnake.unshift(head);

        if (head[0] === food[0] && head[1] === food[1]) {
            setScore(score + 1);
            setFood(getRandomPosition());
            setCurrentEmoji(getRandomEmoji());
        } else {
            newSnake.pop();
        }

        setSnake(newSnake);
    }, [snake, direction, food, gameOver, score, paused, checkCollision, getRandomPosition, getRandomEmoji]);

    useEffect(() => {
        const handleKeyPress = (e) => {
            if (e.key === 'Escape') {
                setPaused(!paused);
            }
            
            if (paused) return;

            switch (e.key) {
                case 'ArrowUp':
                    if (direction !== 'DOWN') setDirection('UP');
                    break;
                case 'ArrowDown':
                    if (direction !== 'UP') setDirection('DOWN');
                    break;
                case 'ArrowLeft':
                    if (direction !== 'RIGHT') setDirection('LEFT');
                    break;
                case 'ArrowRight':
                    if (direction !== 'LEFT') setDirection('RIGHT');
                    break;
                default:
                    break;
            }
        };

        document.addEventListener('keydown', handleKeyPress);
        return () => document.removeEventListener('keydown', handleKeyPress);
    }, [direction, paused]);

    useEffect(() => {
        const gameInterval = setInterval(moveSnake, INITIAL_SPEED);
        return () => clearInterval(gameInterval);
    }, [moveSnake]);

    useEffect(() => {
        // Prevent scrolling when game is mounted
        document.body.style.overflow = 'hidden';
        
        // Restore scrolling when game is unmounted
        return () => {
            document.body.style.overflow = '';
        };
    }, []);

    const handleTouchStart = (e) => {
        setTouchStart({
            x: e.touches[0].clientX,
            y: e.touches[0].clientY
        });
    };

    const handleTouchEnd = (e) => {
        if (!touchStart) return;

        const touchEnd = {
            x: e.changedTouches[0].clientX,
            y: e.changedTouches[0].clientY
        };

        const dx = touchEnd.x - touchStart.x;
        const dy = touchEnd.y - touchStart.y;

        // Determine the direction of the swipe
        if (Math.abs(dx) > Math.abs(dy)) {
            // Horizontal swipe
            if (dx > 0 && direction !== 'LEFT') {
                setDirection('RIGHT');
            } else if (dx < 0 && direction !== 'RIGHT') {
                setDirection('LEFT');
            }
        } else {
            // Vertical swipe
            if (dy > 0 && direction !== 'UP') {
                setDirection('DOWN');
            } else if (dy < 0 && direction !== 'DOWN') {
                setDirection('UP');
            }
        }

        setTouchStart(null);
    };

    const restartGame = () => {
        setSnake([[10, 10]]);
        setFood(getRandomPosition());
        setDirection('RIGHT');
        setGameOver(false);
        setScore(0);
        setCurrentEmoji(FOOD_EMOJIS[0]);
        setPaused(false);
    };

    return (
        <div 
            className={`snake-game-overlay ${darkMode ? 'dark' : ''}`}
            onClick={(e) => {
                // Close game when clicking outside the game container
                if (e.target.className.includes('snake-game-overlay')) {
                    onClose();
                }
            }}
        >
            <div className="snake-game-container">
                <div className="game-header">
                    <div className="score">Score: {score}</div>
                    <div className="controls">
                        <button onClick={() => setPaused(!paused)}>
                            {paused ? '▶️' : '⏸️'}
                        </button>
                        <button onClick={onClose}>✖️</button>
                    </div>
                </div>
                <div 
                    className="game-board"
                    style={{
                        width: GRID_SIZE * CELL_SIZE + 'px',
                        height: GRID_SIZE * CELL_SIZE + 'px'
                    }}
                    onTouchStart={handleTouchStart}
                    onTouchEnd={handleTouchEnd}
                >
                    {snake.map((cell, i) => (
                        <div
                            key={i}
                            className={`snake-cell ${i === 0 ? 'snake-head' : ''}`}
                            style={{
                                left: cell[0] * CELL_SIZE + 'px',
                                top: cell[1] * CELL_SIZE + 'px',
                                width: CELL_SIZE + 'px',
                                height: CELL_SIZE + 'px'
                            }}
                        />
                    ))}
                    <div
                        className="food-cell"
                        style={{
                            left: food[0] * CELL_SIZE + 'px',
                            top: food[1] * CELL_SIZE + 'px',
                            width: CELL_SIZE + 'px',
                            height: CELL_SIZE + 'px'
                        }}
                    >
                        {currentEmoji}
                    </div>
                </div>
                {(gameOver || paused) && (
                    <div className="game-overlay">
                        <div className="game-message">
                            {gameOver ? 'Game Over!' : 'Paused'}
                            <div className="final-score">Score: {score}</div>
                            {gameOver && (
                                <button onClick={restartGame}>Play Again</button>
                            )}
                        </div>
                    </div>
                )}
                <div className="game-instructions">
                    <p>Use arrow keys or swipe to move</p>
                    <p>Press ESC or tap pause to pause</p>
                    <p>Collect the icons! 💻 🚀 ⚡</p>
                </div>
            </div>
        </div>
    );
}

export default SnakeGame; 